/**
 * @file Photo Group toolbar actions component.
 */

import React, { useContext, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import pluralize from 'pluralize';
import { useMediaQuery } from 'react-responsive';

import Button from '../../../../../../components/global/Button';
import { breakpointDown } from '../../../../../../services/responsive/responsive';
import Angle from '../../../../../../components/global/Angle';
import { PhotoSelectorContext } from '../../../../../../components/global/PhotoSelectorContext';
import useOpenModal from '../../../../../../hooks/useOpenModal';
import { PHOTO_DOWNLOAD_MODAL } from '../../../../../../components/global/PhotoDownload';

const propTypes = {
  totalSelectedPhotos: PropTypes.number,
};

const PhotoSelectorToolbar = ({ totalSelectedPhotos }) => {
  const openModal = useOpenModal();
  const isMobile = useMediaQuery(breakpointDown('sm'));
  const {
    selectedPhotos,
    selectionEnabled,
    selectionSettings,
    onResetSelection,
    onSelectAll,
  } = useContext(PhotoSelectorContext);
  const { isSelectAll } = selectionSettings;
  const [openNav, setOpenNav] = useState(false);

  const photoLabel = pluralize('Photo', totalSelectedPhotos);

  const classes = classNames({
    'action-toolbar action-toolbar-bottom-spacer': true,
    'hierarchy-item-photo-selector-toolbar': true,
    'action-toolbar-visible':
      selectionEnabled || (selectedPhotos?.length ?? 0) !== 0 || isSelectAll,
  });
  const buttonClasses = classNames({
    'text-extra-bold': true,
    'text-primary': !isMobile,
    'text-white': isMobile,
  });
  const buttonContainerClasses = classNames({
    'm-0': !isMobile,
    'action-toolbar-actions': !isMobile,
    'photo-group-toolbar-mobile': isMobile,
    visible: isMobile && openNav,
  });

  const actionBtns = totalSelectedPhotos > 0 && (
    <div className={buttonContainerClasses}>
      <span className="action-toolbar-action photo-download">
        <Button
          btnStyle="link"
          className={buttonClasses}
          onClick={() => openModal(PHOTO_DOWNLOAD_MODAL)}
        >
          Download
        </Button>
      </span>
    </div>
  );

  return (
    <div className={classes}>
      <div className="d-flex flex-column align-items-start">
        <h4 className="mb-0">
          {totalSelectedPhotos} {photoLabel.toLowerCase()} selected
        </h4>
        <div className="mt-1">
          <Button
            btnStyle="link"
            className="text-sm mr-2"
            onClick={() => onSelectAll()}
            disabled={isSelectAll}
          >
            Select All
          </Button>
          <Button
            btnStyle="link"
            className="text-sm"
            onClick={() => onResetSelection()}
          >
            {isSelectAll || !!selectedPhotos?.length ? 'Deselect All' : 'Close'}
          </Button>
        </div>
      </div>
      {isMobile ? (
        <div className="d-relative">
          <Button
            btnStyle="primary"
            btnSize="sm"
            onClick={() => setOpenNav(!openNav)}
            onBlur={() => setTimeout(() => setOpenNav(false), 100)}
            disabled={!isSelectAll && (selectedPhotos?.length ?? 0) === 0}
            className=""
          >
            Pick an action{' '}
            {openNav ? (
              <Angle direction="down" className="ml-1" />
            ) : (
              <Angle direction="up" className="ml-1" />
            )}
          </Button>
          {actionBtns}
        </div>
      ) : (
        actionBtns
      )}
    </div>
  );
};

PhotoSelectorToolbar.propTypes = propTypes;

export default PhotoSelectorToolbar;
